import NumberFlow from "@number-flow/react";
import { IconButton, InfoIcon, Typography } from "@povio/ui";
import { cx } from "class-variance-authority";

import { SkeletonWrapper } from "@/components/onboarding/ui/Skeleton";
import { CommonModels } from "@/data/common/common.models";
import { NumberUtils } from "@/util/number.utils";

const YourNumber = ({
  value,
  label,
  numberClassName,
  labelTooltip,
  isUnavailable = false,
  useAnimateValue = false,
  isLoading = false,
  isLifetimeCost = false,
  subtitle,
}: {
  value?: number | null;
  label: string;
  numberClassName?: string;
  labelTooltip: string;
  isUnavailable?: boolean;
  useAnimateValue?: boolean;
  isLoading?: boolean;
  isLifetimeCost?: boolean;
  subtitle?: React.ReactNode;
}) => {
  return (
    <div>
      <Typography
        as="div"
        size="title-4"
        variant="prominent-1"
      >
        {isLoading ? (
          <div className="mb-1 dd:h-4 h-[14px] dd:w-[130px] w-[80px] shrink-0">
            <SkeletonWrapper
              className="dd:h-4 h-[14px] dd:w-[130px] w-[80px]"
              containerClassName="h-[14px] dd:h-4"
            />
          </div>
        ) : (
          <>
            {useAnimateValue && !isUnavailable && (
              <span className="inline-flex items-baseline">
                <span className={numberClassName}>$</span>
                <NumberFlow
                  format={{ maximumFractionDigits: 0 }}
                  value={value || 0}
                  className={numberClassName}
                />
              </span>
            )}
            {!useAnimateValue && !isUnavailable && <>{NumberUtils.formatAsUSCurrency(value || 0)}</>}
            {isUnavailable && "$"}
          </>
        )}
      </Typography>

      <div className="relative flex items-center gap-px">
        <Typography
          size="label-2"
          variant="default"
          className={cx(
            "whitespace-nowrap",
            isLifetimeCost ? "text-interactive-contained-primary-on-idle!" : "text-text-default-3!",
          )}
        >
          {label}
        </Typography>
        <IconButton
          size="none"
          icon={InfoIcon}
          variant="text"
          label={labelTooltip}
          className={cx(
            isLifetimeCost ? "text-interactive-contained-primary-on-idle!" : "text-text-default-3!",
            "[&_svg]:size-4",
          )}
        />
        {subtitle}
      </div>
    </div>
  );
};

export interface ProfileNumbersProps {
  isProcessingWorthData: boolean;
  renderedHistoricalValue: number | null;
  jobPosition: string | null;
  isPrivate: boolean;
  firstName: string;
  renderedYourValue: number | null;
  renderedTargetValue: number | null;
  renderedMissingValue: number | null;
  worthDataStatus: CommonModels.StatusEnum;
}

export const ProfileNumbers = ({
  isProcessingWorthData,
  renderedHistoricalValue,
  jobPosition,
  isPrivate,
  firstName,
  renderedYourValue,
  renderedTargetValue,
  renderedMissingValue,
  worthDataStatus,
}: ProfileNumbersProps) => {
  const isProcessingWorthDataFailed = worthDataStatus === CommonModels.StatusEnum.failed;
  const isWorthDataNoData = worthDataStatus === CommonModels.StatusEnum.no_data;

  return (
    <>
      <div className="flex min-w-auto max-w-48 dd:justify-center rounded-s bg-elevation-fill-default-2 px-3 pt-2 pb-1 hover:bg-interactive-subtle-secondary-hover active:bg-interactive-subtle-secondary-pressed">
        <YourNumber
          value={renderedHistoricalValue}
          label="Industry Baseline"
          labelTooltip={
            jobPosition
              ? `The average salary for ${jobPosition} positions in your city.`
              : "Please let us know your job title and we'll let you know your industry baseline number."
          }
          isUnavailable={isWorthDataNoData || isProcessingWorthDataFailed}
          useAnimateValue
        />
      </div>

      <div className="flex min-w-auto max-w-48 dd:justify-center rounded-s bg-elevation-fill-default-2 px-3 pt-2 pb-1 hover:bg-interactive-subtle-success-idle active:bg-interactive-subtle-success-hover">
        <YourNumber
          value={renderedYourValue}
          label={`${isPrivate ? "Your" : `${firstName}'s`} Number`}
          numberClassName={cx(
            worthDataStatus === CommonModels.StatusEnum.completed ? "text-success-200!" : "text-primary!",
          )}
          labelTooltip="Your optimum annual market value for this position, as determined by your unique qualifications and experiences."
          isUnavailable={isWorthDataNoData || isProcessingWorthDataFailed}
          useAnimateValue
        />
      </div>

      <div className="flex min-w-auto max-w-48 dd:justify-center rounded-s bg-elevation-fill-default-2 px-3 pt-2 pb-1 hover:bg-interactive-subtle-secondary-hover active:bg-interactive-subtle-secondary-pressed">
        <YourNumber
          value={renderedTargetValue}
          label="Target Number"
          labelTooltip="Your achievable salary target within 3-6 months. This value shows what you can realistically earn by adding a high-impact Project to your Profile."
          isUnavailable={isWorthDataNoData || isProcessingWorthDataFailed}
          useAnimateValue
        />
      </div>

      <div className="flex min-w-auto max-w-48 dd:justify-center rounded-s bg-interactive-contained-primary-idle px-3 pt-2 pb-1 text-interactive-contained-primary-on-idle hover:bg-interactive-contained-primary-hover active:bg-interactive-contained-primary-pressed">
        <YourNumber
          value={Math.ceil(renderedMissingValue ?? 0)}
          label="Opportunity Cost"
          labelTooltip="The total lifetime compensation that you are leaving on the  table over 40 years by not realizing your optimum worth."
          isUnavailable={isWorthDataNoData || isProcessingWorthDataFailed}
          isLoading={isProcessingWorthData}
          isLifetimeCost
        />
      </div>
    </>
  );
};
